import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { BehaviorSubject } from 'rxjs';
import { Network } from '@capacitor/network';
import { BaseHttpService } from './base-http.service';
import { ConfigService } from './config.service';

export enum AppModeStatus {
  ONLINE = 'online',
  OFFLINE = 'offline'
}

/**
 * Единый источник признака «онлайн/офлайн» для приложения экспедирования. Статус складывается из двух
 * сигналов:
 * 1) начальное засевание из системного состояния сети при старте ({@link #seedNetworkStatus},
 *    {@code @capacitor/network});
 * 2) живые исходы HTTP-запросов — {@code ErrorInterceptor} переводит в {@code OFFLINE} при обрыве связи
 *    (статус 0) и в {@code ONLINE} при успешном ответе.
 *
 * Смешанный источник надёжнее «чистого» события сети: связь может быть заявлена системой, но фактически
 * не работать (мобильная сеть, портальная авторизация Wi-Fi), и наоборот. На статус завязаны индикатор
 * офлайна, предупреждающие окна и решение отложить действие в очередь синхронизации.
 */
@Injectable({
  providedIn: 'root'
})
export class OfflineModeService extends BaseHttpService {
  // Текущий статус. Обновляется отсюда (засев на старте) и из ErrorInterceptor (по исходу HTTP-запроса).
  // Значение по умолчанию — ONLINE: до первого сигнала считаем связь исправной.
  public appModeStatus$ = new BehaviorSubject<AppModeStatus>(
    AppModeStatus.ONLINE
  );

  constructor(
    protected dbService: NgxIndexedDBService,
    private superHttp: HttpClient,
    private superConfig: ConfigService,
    protected modalCtrl: ModalController
  ) {
    super(superHttp, superConfig);
    this.seedNetworkStatus();
  }

  // Засеваем текущий статус сети на старте. @capacitor/network НЕ эмитит событие при подписке (только
  // при смене состояния), поэтому холодный старт без сети иначе оставался бы в дефолтном ONLINE до
  // первого проваленного HTTP-запроса — индикатор офлайна и зависящие от него действия вводили бы в
  // заблуждение, особенно на iOS/Android.
  private seedNetworkStatus(): void {
    Network.getStatus()
      .then(status =>
        this.appModeStatus$.next(
          status.connected ? AppModeStatus.ONLINE : AppModeStatus.OFFLINE
        )
      )
      .catch(() => {
        // getStatus может быть недоступен в отдельных web-окружениях — оставляем дефолт ONLINE.
      });
  }
}
